import { Controller } from "@hotwired/stimulus";

import {
  clearPending,
  keyFor,
  pendingFor,
  scheduleSubmit,
  setPending,
} from "../../shared/manual_order_line_quantities";

/**
 * ONE QUANTITY MODIFIER ON A LINE (CAL-509): the count,
 * the two buttons around it, and whether the option
 * reads as chosen.
 *
 * It shipped as a bare `<input type="number">`. The
 * browser's spinner is two 8px arrows for a thumb on a
 * tablet, and a count of 3 looked exactly like a count
 * of 0, so the only way to read "Elige tu salsa" was to
 * read every number on it.
 *
 * THE COUNT MOVES ON THE PRESS, never on the response.
 * The request that follows is debounced in
 * `manual_order_line_quantities`, which also remembers
 * the value across the frame replacement that answers
 * it: see the note at the top of that file for why the
 * memory cannot live here.
 */
export default class extends Controller {
  static targets = ["field", "decrement", "increment"];

  static classes = ["selected", "resting"];

  static values = {
    lineId: String,
    modifierId: String,
    max: Number,
    formId: String,
    // Whether the GROUP this option sits in is already
    // holding as many options as its `max` allows. Only
    // the step off zero is refused by it: an option
    // already on the line occupies its slot whatever
    // the count.
    groupFull: Boolean,
  };

  connect() {
    const entry = pendingFor(this.key);
    const server = this.count;

    // WHAT THE LINE ACTUALLY HOLDS, captured before the
    // merchant can empty the box. `count` reads the
    // FIELD, so once they backspace it answers 0, and
    // blur would paint a zero over a modifier the server
    // still has (CAL-509).
    this.settled = server;

    if (!entry) return this.paint(server);

    // Either the server agreed, or the server answered
    // something else about a value it was told: both
    // end the same way, with the server's number on
    // screen and nothing owed.
    if (entry.value === server || entry.submitted) {
      clearPending(this.key);
      return this.paint(server);
    }

    // A press the merchant made while the request that
    // produced this element was in flight.
    //
    // Through `commit`, so the GROUP hears about it: the
    // server row this element was drawn from predates
    // the press, and may still be carrying the pill that
    // press displaced. The group element connects ahead
    // of its children, so its listener is already there.
    this.commit(entry.value);
  }

  increase() {
    this.commit(this.count + 1);
  }

  decrease() {
    this.commit(this.count - 1);
  }

  /**
   * Typing straight into the box goes through the same
   * debounce as the buttons.
   *
   * The box is left alone while it holds something we
   * can read, so a merchant clearing it to type "12"
   * is not fighting a controller writing "0" back
   * between their keystrokes. `normalize` on blur puts
   * a real number back.
   */
  typed() {
    const digits = this.fieldTarget.value.replace(/[^0-9]/g, "");

    if (digits !== this.fieldTarget.value) {
      this.fieldTarget.value = digits;
    }

    // AN EMPTY BOX IS AN UNFINISHED NUMBER, NOT A ZERO,
    // and this return is the other half of the comment
    // above. Leaving the box alone was never enough on
    // its own: committing zero here schedules a submit,
    // and the response REPLACES the lines card, so a
    // merchant who backspaces to retype and pauses for
    // 350ms has the modifier taken off the line, the
    // input they were typing into destroyed, and their
    // next digit goes nowhere. Blur decides what an
    // emptied box meant; `normalize` puts the real
    // number back (CAL-509).
    //
    // Typing a literal "0" still removes the option:
    // that is a number the merchant finished.
    if (digits === "") return;

    const next = this.clamp(Number(digits));

    if (String(next) !== digits) {
      this.write(next);
    }

    this.commit(next, { write: false });
  }

  /**
   * Blur decides what an emptied box meant, and the
   * answer is "nothing": the count goes back to what the
   * line holds. `pendingOrCount` falls through to
   * `count`, which reads the FIELD, so on an emptied box
   * it answers 0 and would paint a zero over a modifier
   * the server still carries, with nothing submitted to
   * make it true. `settled` is that truth (CAL-509).
   */
  normalize() {
    const entry = pendingFor(this.key);

    this.write(entry ? entry.value : this.settled);
  }

  /**
   * The group emptied this option to make room for the
   * one the merchant just picked. It does NOT announce
   * itself back to the group (that is the loop) and it
   * does not schedule a submit of its own: the press
   * that displaced it is already sending one, and this
   * value rides in the same body.
   */
  clear() {
    if (this.count === 0) return;

    setPending(this.key, 0);
    this.write(0);
    this.paint(0);
  }

  limit(event) {
    this.groupFullValue = Boolean(event.detail.full);
    this.paint(this.count);
  }

  commit(next, { write = true } = {}) {
    const value = this.clamp(next);

    setPending(this.key, value);
    this.settled = value;
    if (write) this.write(value);
    this.paint(value);

    // Synchronously, so the group has cleared whatever
    // its `max` cannot hold before the debounce fires.
    this.dispatch("changed", { detail: { value } });

    scheduleSubmit(this.formIdValue, this.lineIdValue);
  }

  /**
   * The selected state and the two bounds, together,
   * because they are the same answer: what the count
   * is now.
   */
  paint(value) {
    const selected = value > 0;

    if (this.hasSelectedClass && this.hasRestingClass) {
      this.element.classList.remove(
        ...(selected ? this.restingClasses : this.selectedClasses)
      );
      this.element.classList.add(
        ...(selected ? this.selectedClasses : this.restingClasses)
      );
    }

    if (this.hasDecrementTarget) {
      this.decrementTarget.disabled = value <= 0;
    }

    if (this.hasIncrementTarget) {
      this.incrementTarget.disabled =
        value >= this.maxValue ||
        (this.groupFullValue && value === 0);
    }
  }

  write(value) {
    this.fieldTarget.value = String(value);
  }

  clamp(value) {
    if (!Number.isFinite(value)) return 0;

    return Math.min(Math.max(Math.trunc(value), 0), this.maxValue);
  }

  get key() {
    return keyFor(this.lineIdValue, this.modifierIdValue);
  }

  get count() {
    const digits = this.fieldTarget.value.replace(/[^0-9]/g, "");

    return digits === "" ? 0 : Number(digits);
  }

  get pendingOrCount() {
    const entry = pendingFor(this.key);

    return entry ? entry.value : this.count;
  }
}
